import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FontWeight, Heading, LiliButton } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import ContentWrapper from '../../../../generic-components/ContentWrapper';
import MainContent from '../../../../generic-components/Layout/MainContent';
import SubContent from '../../../../generic-components/Layout/SubContent';
import { WIDGET_STYLES } from '../../../dashboard/constants';
import { PROFIT_AND_LOSS_STYLES, ReportFlowStep } from '../../constants';
import { CategorizeContainer } from '../../../categorizeTransacrions/CategorizeContainer';
import { CategorizeSummary } from './categorizeSummary';
import { ProfitAndLossContext } from '../../pages';
import { ITransaction } from '../../../../types';
import { FeatureAccess } from '../../../../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';
import { featureAccess, LimitedFeatureType } from '../../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../../constants/general';

interface INavItem {
  id: string;
  title: string;
  titleClassName: string;
  titleClassNameSelected: string;
  wrapperClassName: string;
  wrapperClassNameSelected: string;
  isDisabled?: boolean;
}

export const CategorizedWrapper = () => {
  const { t } = useTranslation('reportsEN');
  const FlowContext = useContext(ProfitAndLossContext);
  const [activeCard, setActiveCard] = useState<ITransaction | undefined>(undefined);
  const categorizePermission = featureAccess(
    FEATURE_FLAG.CATEGORIZATION.FEATURE,
    LimitedFeatureType.TransactionListCategorize,
  );
  const [isCategorizeFinish, setIsCategorizeFinish] = useState<boolean>(false);
  const [prevPathName, setPrevPathName] = useState<string>('');
  const [shouldResetSteps, setShouldResetSteps] = useState<boolean>(false);
  const [categorizationState, setCategorizationState] = useState<INavItem[]>([]);
  const location = useLocation();

  useEffect(() => {
    setPrevPathName(location.pathname);
    setShouldResetSteps(true);
  }, []);

  useEffect(() => {
    if (location.pathname !== prevPathName && shouldResetSteps) {
      FlowContext?.setActiveStep(ReportFlowStep.INTRO);
    }
  }, [location.pathname]);

  const isReportValid = useMemo(
    () => categorizationState && categorizationState[0]?.isDisabled && categorizationState[1]?.isDisabled,
    [categorizationState],
  );

  const handleCardsAmountChange = (amount: number) => {
    setIsCategorizeFinish(!amount);
  };

  return (
    <ContentWrapper>
      <MainContent
        className={
          isCategorizeFinish
            ? PROFIT_AND_LOSS_STYLES.CATEGORIZED_CONTAINER
            : PROFIT_AND_LOSS_STYLES.NOT_CATEGORIZED_CONTAINER
        }
      >
        <div className='w-[432px] flex flex-col items-center mx-auto'>
          <Heading.H7
            id='report-categorization-title'
            fontWeight={FontWeight.MEDIUM}
            className='text-text-main-primary  mt-[54px]'
          >
            {t('REPORTS.CATEGORIZATION.TITLE')}
          </Heading.H7>
          <Heading.H10
            id='report-categorization-title'
            className='text-text-main-tertiary text-center mt-[10px] mb-[28px]'
          >
            {t('REPORTS.CATEGORIZATION.SUBTITLE')}
          </Heading.H10>
          <CategorizeContainer
            isReadOnlyMode={categorizePermission !== FeatureAccess.READ_WRITE}
            toggleData={FlowContext?.toggleData}
            filterRange={{
              startDate: new Date(FlowContext?.filterObject.period?.startDate || '').toISOString(),
              endDate: new Date(FlowContext?.filterObject.period?.endDate || '').toISOString(),
            }}
            handleFinishCategorize={() => {}}
            onMutateFinish={() => {}}
            isInitialLoading={false}
            leftToCategorize={handleCardsAmountChange}
            shouldHideHeader
            categorizationProgress={setCategorizationState}
            containerClassName={WIDGET_STYLES.CATEGORIZATION.WRAPPER}
            navSwitchClassName={WIDGET_STYLES.CATEGORIZATION.NAV_SWITCH_WRAPPER}
            onActiveTrxChange={(trxData: ITransaction) => {
              setActiveCard({ ...trxData });
            }}
            actionTypesToHide={['categorizeNotNow']}
          />
          <LiliButton
            id='generateTheReportButton'
            title={t('REPORTS.CATEGORIZATION.GENERATE_BUTTON')}
            disabled={categorizePermission !== FeatureAccess.READ_WRITE || !isReportValid}
            className='max-w-[200px]'
            onClick={() => FlowContext?.setActiveStep(ReportFlowStep.REPORT)}
          />
        </div>
      </MainContent>
      {!isCategorizeFinish && (
        <SubContent className='bg-bw00 relative rounded-lg p-8 mt-[16px] pt-[26px] h-[100%] basis-[30%] overflow-y-hidden fadeIn'>
          <CategorizeSummary isReadOnly={categorizePermission !== FeatureAccess.READ_WRITE} activeCard={activeCard} />
        </SubContent>
      )}
    </ContentWrapper>
  );
};
